<template lang="pug">
  .product-card(v-if="!error && meta && meta.price")
    .thumb
      StaticImage(:width="140" :height="140" :src="meta.thumbnail" :alt="meta.name")
    .name
      .product-name(v-html="meta.name")
      product-stars(:product="{sku: meta.linkSku}")
      component.link.product-link(:to="meta.url" v-if="meta.productId" :is="meta.productId ? 'rogue-link' : 'div'" :productId="meta.productId") {{ __('View Product') }}
    .price
      .total-price {{ rogue.currency(total ?? 0) }}
      .qty-at {{ `${meta.qty} @ ${rogue.currency(price || meta.price)}` }}
    .btns
      RogueButton.btn(color="black" @click="addTo('cart')" v-if="status === ''") {{ __('Add to Cart') }}
      bin-email-input(:bin="BIN")
      RogueButton.btn(v-if="status === 'bin'" :loading="BIN.loading" @click="clickBinButton($el)")
        bin-status-text(:bin="BIN")
      RogueButton.btn(color="gray" :disabled="true" v-if="status === 'oos'") {{__('Item is Unavailable')}}
      add-to-wishlist(:buyRequest="toAdd" v-if="toAdd")
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import productStars from '@/js/components/product-stars.vue'
import { computePrice } from '@/js/helpers/price-utils'
import { getBuyRequestMeta } from '@/js/helpers/buy-request'
import AddToWishlist from '@/js/components/add-to-wishlist.vue'
import stockStatus from '@/js/helpers/stockStatus'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import { useReviewsStore } from '@/js/stores/reviewsStore'
import { useBackInStock } from '@/js/components/bin/useBackInStock'
import BinEmailInput from '@/js/components/bin/bin-email-input.vue'
import BinStatusText from '@/js/components/bin/bin-status-text.vue'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest, BuyRequestMeta } from '@/js/services/types/order'

const props = defineProps<{
  /** Absent while the parent is still loading, which renders the card as a placeholder. */
  buyRequest?: BuyRequest
}>()

const listStore = useListStore()
const productStore = useProductStore()
const reviewsStore = useReviewsStore()
const rogue = useRogue()
const { BIN, clickBinButton } = useBackInStock(props.buyRequest as BuyRequest)

const error = ref(false)
const price = ref<number | null>(null)
const total = ref<number | null>(null)
const toAdd = ref<BuyRequest | null>(null)
const status = ref<ReturnType<typeof stockStatus.forBuyRequest> | null>(null)

// `url` and `productId` are read by the template but are not part of BuyRequestMeta.
const meta = computed<(BuyRequestMeta & { url?: string, productId?: number }) | null>(() => {
  try {
    return props.buyRequest?.productId ? getBuyRequestMeta(props.buyRequest, rogue) : null
  } catch (e) {
    return null
  }
})

watch(() => props.buyRequest, (newVal) => {
  total.value = null
  price.value = null
  toAdd.value = null
  if (newVal?.productId) {
    toAdd.value = JSON.parse(JSON.stringify(newVal))
    computePrice(Object.assign({}, newVal, { qty: 1 }), rogue).then(val => {
      total.value = val * (newVal.qty ?? 0)
      price.value = val
      status.value = stockStatus.forBuyRequest(newVal, productStore.products)
    }).catch(() => {
      error.value = true
    })
  }
}, { immediate: true })

function addTo(list = 'cart') {
  listStore.addListItems(rogue, list, [toAdd.value!])
}

async function load() {
  await reviewsStore.loadSummaries(meta.value?.linkSku ? [meta.value.linkSku] : [], rogue)
}

onMounted(async () => {
  await load()
})
</script>

<style lang="scss" scoped>
.product-card {
  border: 1px solid $color-gray-light;
  padding: $space-s;
  display: grid;
  gap: $space-xxs;
  grid-template-columns: 85px 1fr minmax(170px, 1fr);
  grid-template-rows: 85px 1fr;
  grid-template-areas:
      "thumb name name"
      "price price price"
      "btns btns btns";

  @include tablet(){
    grid-template-columns: 140px 1fr min-content;
    gap: $space-xs;
    grid-template-areas:
        "thumb name price"
        "thumb btns btns";
  }

  @include all-desktop() {
    gap: $space-xs;
    grid-template-columns: 140px 1fr min-content;
    // grid-template-rows: calc(60px - #{$space-xs} / 2) calc(80px - #{$space-xs} / 2);
    grid-template-rows: auto auto;
    grid-template-areas:
        "thumb name price"
        "thumb btns btns";
  }

  @include desktop(){
    grid-template-columns: 100px 1fr min-content;
  }
}

.name {
  grid-area: name;

  .product-name {
    font-size: $size-xs;
    margin-bottom: $space-xxxs;
  }

  .product-stars-container {
    margin-bottom: 0;
  }

  .product-link {
    font-size: $size-xxs;
  }
}

.thumb {
  grid-area: thumb;
}

.price {
  grid-area: price;
  white-space: nowrap;

  @include mobile(){
    display: flex;
    justify-content: space-between;
  }

  @include not-mobile() {
    text-align: right;
  }

  .total-price {
    font-family: $font-condensed;
    font-size: $size-s;
    font-weight: bold;
    margin-bottom: $space-xxxs;
  }

  .qty-at {
    font-size: $size-xxs;
  }
}

.btns {
  grid-area: btns;

  .btn {
    margin-bottom: $space-xs;
    width: 100%;

    @include not-mobile() {
      width: 160px;
    }
  }
  :deep() {
    .wishlist {
      margin-top: 0;
    }
  }
}

.link, .wishlist-message {
  color: $color-black;
  font-size: $size-xs;
  display: flex;
  align-items: center;

  .icon {
    width: 15px;
    height: 15px;
    margin-right: $space-xxs;
  }
}
</style>
